import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState, TablePageShell } from '@/shared/ui';

import {
  useCreateProductBarcode,
  useDeleteProductBarcode,
  useProductBarcodes,
  useSetPrimaryProductBarcode,
} from '../../hooks';
import type { ToastSeverity } from '../../lib/useToast';
import {
  PRODUCT_BARCODE_SOURCE_LABELS,
  PRODUCT_BARCODE_TYPE_LABELS,
  productBarcodeSourceColor,
} from '../../model/options';
import type { ProductBarcode } from '../../model/types';
import { ProductBarcodeFormDialog } from './ProductBarcodeFormDialog';

export interface ProductBarcodesPanelProps {
  productId: string;
  canManage: boolean;
  onNotify: (severity: ToastSeverity, message: string) => void;
}

/**
 * The "Barcodes" section on `ProductEditPage` (user-story-merchant-portal-039, existing products
 * only). Lists the product's ACTIVE barcodes (RETIRED ones are hidden — they are history, not a
 * management target) with add/generate/set-primary/retire actions, all gated on `canManage`; a
 * view-only user (`catalog.view` only) sees the read-only list with no action affordances.
 */
export function ProductBarcodesPanel({ productId, canManage, onNotify }: ProductBarcodesPanelProps) {
  const { t } = useTranslation('catalog');
  const query = useProductBarcodes(productId);
  const generate = useCreateProductBarcode();
  const setPrimary = useSetPrimaryProductBarcode();
  const retire = useDeleteProductBarcode();

  const [addOpen, setAddOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; barcode: ProductBarcode } | null>(
    null,
  );
  const [confirmDelete, setConfirmDelete] = useState<ProductBarcode | null>(null);

  const barcodes = (query.data ?? []).filter((b) => b.status !== 'RETIRED');

  const handleGenerate = () => {
    generate.mutate(
      { productId, body: { generate: true } },
      {
        onSuccess: (barcode) => onNotify('success', t('barcodes.generateSuccess', { value: barcode.value })),
        onError: (error) => onNotify('error', error.message),
      },
    );
  };

  const handleSetPrimary = () => {
    if (!menuAnchor) return;
    const { barcode } = menuAnchor;
    setMenuAnchor(null);
    setPrimary.mutate(
      { productId, barcodeId: barcode.id },
      {
        onSuccess: () => onNotify('success', t('barcodes.setPrimarySuccess', { value: barcode.value })),
        onError: (error) => onNotify('error', error.message),
      },
    );
  };

  const handleDelete = () => {
    if (!confirmDelete) return;
    const barcode = confirmDelete;
    retire.mutate(
      { productId, barcodeId: barcode.id },
      {
        onSuccess: () => {
          onNotify('success', t('barcodes.deleteSuccess', { value: barcode.value }));
          setConfirmDelete(null);
        },
        onError: (error) => {
          onNotify('error', error.message);
          setConfirmDelete(null);
        },
      },
    );
  };

  return (
    <Stack spacing={1.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Typography variant="body2" color="text.secondary">
          {t('barcodes.subtitle')}
        </Typography>
        {canManage ? (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<AddRoundedIcon fontSize="small" />}
              onClick={() => setAddOpen(true)}
            >
              {t('common:action.add')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                generate.isPending ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <AutoAwesomeRoundedIcon fontSize="small" />
                )
              }
              disabled={generate.isPending}
              onClick={handleGenerate}
            >
              {t('barcodes.generateAction')}
            </Button>
          </Stack>
        ) : null}
      </Stack>

      {query.isLoading ? (
        <LoadingState label={t('barcodes.loading')} />
      ) : query.isError ? (
        <ErrorState
          title={t('barcodes.loadError.title')}
          message={query.error?.message ?? t('common:error.unknown')}
          onRetry={() => void query.refetch()}
        />
      ) : barcodes.length === 0 ? (
        <EmptyState
          title={t('barcodes.empty.title')}
          description={t('barcodes.empty.description')}
          icon={<QrCode2RoundedIcon />}
        />
      ) : (
        <TablePageShell>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('barcodes.table.value')}</TableCell>
                  <TableCell>{t('barcodes.table.type')}</TableCell>
                  <TableCell>{t('barcodes.table.source')}</TableCell>
                  {canManage ? <TableCell align="right" /> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {barcodes.map((barcode) => (
                  <TableRow key={barcode.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {barcode.value}
                        </Typography>
                        {barcode.primary ? (
                          <Chip size="small" color="success" label={t('barcodes.primaryBadge')} />
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(PRODUCT_BARCODE_TYPE_LABELS[barcode.type] ?? barcode.type)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={productBarcodeSourceColor(barcode.source)}
                        label={t(PRODUCT_BARCODE_SOURCE_LABELS[barcode.source] ?? barcode.source)}
                      />
                    </TableCell>
                    {canManage ? (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          aria-label={t('barcodes.table.rowActionsAria', { value: barcode.value })}
                          onClick={(e) => setMenuAnchor({ el: e.currentTarget, barcode })}
                        >
                          <MoreVertRoundedIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TablePageShell>
      )}

      <Menu anchorEl={menuAnchor?.el ?? null} open={menuAnchor !== null} onClose={() => setMenuAnchor(null)}>
        <MenuItem disabled={menuAnchor?.barcode.primary ?? false} onClick={handleSetPrimary}>
          {t('barcodes.setPrimaryAction')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAnchor) setConfirmDelete(menuAnchor.barcode);
            setMenuAnchor(null);
          }}
        >
          {t('common:action.delete')}
        </MenuItem>
      </Menu>

      <ProductBarcodeFormDialog
        open={addOpen}
        productId={productId}
        hasExistingBarcodes={barcodes.length > 0}
        onClose={() => setAddOpen(false)}
        onResult={onNotify}
      />

      <Dialog
        open={confirmDelete !== null}
        onClose={() => (!retire.isPending ? setConfirmDelete(null) : undefined)}
      >
        <DialogTitle>{t('barcodes.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDelete ? t('barcodes.deleteConfirmBody', { value: confirmDelete.value }) : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={retire.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={retire.isPending}
            startIcon={retire.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('common:action.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
